/* eslint-disable react/require-default-props */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import AppModal from 'src/components/common/AppModal';
import { AsyncSelect } from 'src/components/common/Select';
import CollectionSelect from 'src/components/common/CollectionSelect';

import SelectionSharingShortcuts from 'src/apps/mydb/elements/list/selectionActions/SelectionSharingShortcuts';
import PermissionIcons from 'src/apps/mydb/collections/PermissionIcons';

import UIStore from 'src/stores/alt/stores/UIStore';
import UserStore from 'src/stores/alt/stores/UserStore';
import UsersFetcher from 'src/fetchers/UsersFetcher';
import { observer } from 'mobx-react';
import { StoreContext } from 'src/stores/mobx/RootStore';

import { selectUserOptionFormater } from 'src/utilities/selectHelper';
import { filterParamsFromUIState } from 'src/utilities/collectionUtilities';
import { PermissionConst } from 'src/utilities/PermissionConst';

function SelectionShareModal({
  title = 'Sharing of Elements',
  collectionId = null,
  collectionShareId = null,
  shareType = 'new',
  collectionPermissions = {
    permissionLevel: 0,
    sampleDetailLevel: 0,
    reactionDetailLevel: 0,
    wellplateDetailLevel: 0,
    screenDetailLevel: 0,
    elementDetailLevel: 10,
  },
  showUserSelect = true,
  onHide,
}) {
  const collectionsStore = useContext(StoreContext).collections;
  const [permissions, setPermissions] = useState(collectionPermissions);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newCollectionLabel, setNewCollectionLabel] = useState('');
  const [parentCollection, setParentCollection] = useState(null);
  const [applyToSubcollections, setApplyToSubcollections] = useState(false);
  const [includeNewSubcollections, setIncludeNewSubcollections] = useState(false);
  const defaultRole = 'Pick a sharing role';
  const [role, setRole] = useState(defaultRole);

  const currentUser = (UserStore.getState() && UserStore.getState().currentUser) || {};
  const uiState = UIStore.getState();
  const displayWarning = Number(permissions.permissionLevel) === PermissionConst.PassOwnership;
  // Ownership is an offer to a single person; the backend rejects a group (or multiple recipients)
  // at level 5 with a 422 (see prevent_invalid_ownership_offer! in collection_share_api.rb). Flag it
  // here so the user gets an explanation instead of a bare error, and block the submit.
  const invalidOwnershipOffer = displayWarning && showUserSelect
    && ((selectedUsers || []).some((u) => u.type === 'Group') || (selectedUsers || []).length > 1);
  const canSubmit = (!showUserSelect || (selectedUsers != null && selectedUsers.length > 0))
    && !invalidOwnershipOffer;
  const submitTitle = shareType === 'edit' ? 'Edit Permissions' : 'Create Shared Collection';

  const handleSharing = () => {
    const permissionsParams = {
      permission_level: permissions.permissionLevel,
      element_detail_level: permissions.elementDetailLevel,
      reaction_detail_level: permissions.reactionDetailLevel,
      sample_detail_level: permissions.sampleDetailLevel,
      screen_detail_level: permissions.screenDetailLevel,
      wellplate_detail_level: permissions.wellplateDetailLevel,
      celllinesample_detail_level: 10,
      devicedescription_detail_level: 10,
      sequencebasedmacromoleculesample_detail_level: 10,
      researchplan_detail_level: 10,
    };

    if (shareType === 'new') {
      // create new collection with shared elements
      const params = {
        uiState: filterParamsFromUIState(uiState),
        users: selectedUsers,
        permissions: permissionsParams,
        label: newCollectionLabel.trim(),
        parentId: parentCollection?.id ?? null,
      };
      collectionsStore.collectionShareForElements(params);
    } else if (shareType === 'create') {
      // create collection share of collectionId
      const params = {
        collection_id: collectionId,
        user_ids: selectedUsers.map((u) => u.id),
        apply_to_subcollections: applyToSubcollections,
        ...permissionsParams
      };
      collectionsStore.addCollectionShare(params);
    } else if (shareType === 'edit') {
      // edit permissions of collection share
      const params = {
        id: collectionShareId,
        apply_to_subcollections: applyToSubcollections,
        include_new_subcollections: includeNewSubcollections,
        ...permissionsParams
      };
      collectionsStore.updateCollectionShare(collectionShareId, params);
    }

    onHide();
  };

  const handleShortcutChange = (e) => {
    const val = e.target.value;
    let permissionsAndDetailLevel = {};

    switch (val) {
      case 'user':
        permissionsAndDetailLevel = SelectionSharingShortcuts.user();
        break;
      case 'partner':
        permissionsAndDetailLevel = SelectionSharingShortcuts.partner();
        break;
      case 'collaborator':
        permissionsAndDetailLevel = SelectionSharingShortcuts.collaborator();
        break;
      case 'reviewer':
        permissionsAndDetailLevel = SelectionSharingShortcuts.reviewer();
        break;
      case 'supervisor':
        permissionsAndDetailLevel = SelectionSharingShortcuts.supervisor();
        break;
      default:
        permissionsAndDetailLevel = {};
    }
    permissionsAndDetailLevel.elementDetailLevel = permissions.elementDetailLevel;
    setPermissions(permissionsAndDetailLevel);
    setRole(val);
  };

  const handlePermissionLevelChange = (e) => {
    // <option value> is always a string; the API and the shortcuts both deal in integers.
    const val = Number(e.target.value);
    setPermissions({ ...permissions, permissionLevel: val });
    setRole(defaultRole);
  };

  const handleDetailLevelChange = (e, elementType) => {
    const val = e.target.value;
    setPermissions({ ...permissions, [`${elementType}DetailLevel`]: val });
    setRole(defaultRole);
  };

  const handleSelectUser = (val) => {
    setSelectedUsers(val);
  };

  const loadUserByName = (input) => {
    if (!input) { return Promise.resolve([]); }

    return UsersFetcher.fetchUsersByName(input, 'Person,Group')
      .then((res) => selectUserOptionFormater(
        { data: res, withType: true, currentUserId: currentUser.id }
      )).catch((errorMessage) => {
        console.log(errorMessage);
      });
  };

  const userSelect = () => {
    if (!showUserSelect) return null;

    return (
      <Form.Group className="mb-3">
        <Form.Label>Select Users to share with</Form.Label>
        <AsyncSelect
          id="share-users-select"
          isMulti
          value={selectedUsers}
          matchProp="name"
          loadOptions={loadUserByName}
          onChange={handleSelectUser}
        />
      </Form.Group>
    );
  };

  return (
    <AppModal
      show
      onHide={onHide}
      title={title}
      primaryActionLabel={submitTitle}
      onPrimaryAction={handleSharing}
      primaryActionDisabled={!canSubmit}
      enforceFocus={false}
    >
      <Form>
        {shareType === 'new' && (
          <>
            <Form.Group className="mb-3" controlId="newCollectionLabel">
              <Form.Label>New collection name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Leave empty to use “My project with …”"
                value={newCollectionLabel}
                onChange={(e) => setNewCollectionLabel(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="newCollectionParent">
              <Form.Label>Parent collection (optional)</Form.Label>
              <CollectionSelect
                value={parentCollection}
                withShared={false}
                onChange={setParentCollection}
              />
            </Form.Group>
          </>
        )}
        <Form.Group className="mb-3" controlId="shortcutSelect">
          <Form.Label>Role</Form.Label>
          <Form.Select
            placeholder="Pick a sharing role (optional)"
            value={role || ''}
            onChange={(e) => handleShortcutChange(e)}
          >
            <option value="Pick a sharing role">Pick a sharing role (optional)</option>
            <option value="user">User</option>
            <option value="partner">Partner</option>
            <option value="collaborator">Collaborator</option>
            <option value="reviewer">Reviewer</option>
            <option value="supervisor">Supervisor</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="permissionLevelSelect" id="permissionLevelSelect">
          <Form.Label>Permission level</Form.Label>
          <Form.Select
            onChange={(e) => handlePermissionLevelChange(e)}
            value={permissions.permissionLevel ?? ''}
          >
            <option value={PermissionConst.ReadElements}>Read elements</option>
            <option value={PermissionConst.EditElements}>Edit elements (+ read)</option>
            <option value={PermissionConst.AddElements}>Add elements (+ read, edit)</option>
            <option value={PermissionConst.RemoveElements}>Remove elements (+ read, edit, add)</option>
            <option value={PermissionConst.ManageShares}>Manage shares (+ read, edit, add, remove)</option>
            <option value={PermissionConst.PassOwnership}>Pass ownership (+ all of the above)</option>
          </Form.Select>
          <Form.Text className="d-block">
            <span className="me-2">Grants:</span>
            <PermissionIcons pl={Number(permissions.permissionLevel) || 0} />
            <span className="ms-2 text-muted">Each level also grants all the levels below it.</span>
          </Form.Text>
          {displayWarning && (
            <Form.Text className="d-block">
              <i className="fa fa-exclamation-circle ms-1" aria-hidden="true" />
              Transferring ownership applies to all sub-collections.
            </Form.Text>
          )}
          {invalidOwnershipOffer && (
            <Form.Text className="d-block text-danger">
              <i className="fa fa-exclamation-triangle ms-1" aria-hidden="true" />
              Ownership can only be transferred to a single person, not a group or several users.
            </Form.Text>
          )}
        </Form.Group>
        {(shareType === 'create' || shareType === 'edit') && (
          <Form.Group className="mb-3" controlId="applyToSubcollections">
            <Form.Check
              type="checkbox"
              label="Apply these share settings to all sub-collections"
              checked={applyToSubcollections}
              onChange={(e) => {
                setApplyToSubcollections(e.target.checked);
                if (!e.target.checked) { setIncludeNewSubcollections(false); }
              }}
            />
            {shareType === 'edit' && applyToSubcollections && (
              <Form.Check
                className="ms-4 mt-1"
                type="checkbox"
                id="includeNewSubcollections"
                label="Also include sub-collections not already shared with this user (by default, only sub-collections already shared with them are updated)"
                checked={includeNewSubcollections}
                onChange={(e) => setIncludeNewSubcollections(e.target.checked)}
              />
            )}
          </Form.Group>
        )}
        <Form.Group className="mb-3" controlId="sampleDetailLevelSelect">
          <Form.Label>Sample detail level</Form.Label>
          <Form.Select
            onChange={(e) => handleDetailLevelChange(e, 'sample')}
            value={permissions.sampleDetailLevel || ''}
          >
            <option value="0">Molecular mass of the compound, external label</option>
            <option value="1">Molecule, structure</option>
            <option value="2">Analysis Result + Description</option>
            <option value="3">Analysis Datasets</option>
            <option value="10">Everything</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="reactionDetailLevelSelect">
          <Form.Label>Reaction detail level</Form.Label>
          <Form.Select
            onChange={(e) => handleDetailLevelChange(e, 'reaction')}
            value={permissions.reactionDetailLevel || ''}
          >
            <option value="0">Observation, description, calculation</option>
            <option value="10">Everything</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="wellplateDetailLevelSelect">
          <Form.Label>Wellplate detail level</Form.Label>
          <Form.Select
            onChange={(e) => handleDetailLevelChange(e, 'wellplate')}
            value={permissions.wellplateDetailLevel || ''}
          >
            <option value="0">Wells (Positions)</option>
            <option value="1">Readout</option>
            <option value="10">Everything</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="screenDetailLevelSelect">
          <Form.Label>Screen detail level</Form.Label>
          <Form.Select
            onChange={(e) => handleDetailLevelChange(e, 'screen')}
            value={permissions.screenDetailLevel || ''}
          >
            <option value="0">Name, description, condition, requirements</option>
            <option value="10">Everything</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="elementDetailLevelSelect">
          <Form.Label>Element detail level</Form.Label>
          <Form.Select
            onChange={(e) => handleDetailLevelChange(e, 'element')}
            value={permissions.elementDetailLevel || ''}
          >
            <option value="10">Everything</option>
          </Form.Select>
        </Form.Group>
        {userSelect()}
      </Form>
    </AppModal>
  );
}

export default observer(SelectionShareModal);

SelectionShareModal.propTypes = {
  title: PropTypes.string,
  collectionId: PropTypes.number,
  collectionShareId: PropTypes.number,
  shareType: PropTypes.string,
  showUserSelect: PropTypes.bool,
  collectionPermissions: PropTypes.shape({
    permissionLevel: PropTypes.number,
    sampleDetailLevel: PropTypes.number,
    reactionDetailLevel: PropTypes.number,
    wellplateDetailLevel: PropTypes.number,
    screenDetailLevel: PropTypes.number,
    elementDetailLevel: PropTypes.number,
  }),
  onHide: PropTypes.func.isRequired,
};
